import { useAbility } from '../contexts/AbilityContext';
import { ACTIONS, SUBJECTS } from '../constants/permissions';
import { PROJECT_PURPOSE_TYPES } from '../constants/projectPurposes';

/**
 * Custom hook that provides project permission checking functions
 * @returns {Object} Object containing permission checking functions
 */
export const useProjectPermissions = () => {
  const ability = useAbility();

  /**
   * Check if user can manage all projects (has unconditional manage permission)
   */
  const canManageAllProjects = () => {
    const rules = ability.rulesFor(ACTIONS.MANAGE, SUBJECTS.PROJECT);
    // Only return true if there's a rule without conditions (truly global access)
    return rules.some(rule => !rule.conditions);
  };

  /**
   * Check if user can manage project based on cluster permissions
   */
  const canManageProjectBasedOnCluster = (project) => {
    if (!project?.businessUnit?.cluster?.id) return false;

    const clusterId = project.businessUnit.cluster.id;

    const projectObject = {
      clusterId: clusterId,
      __type: SUBJECTS.PROJECT
    };

    return ability.can(ACTIONS.MANAGE, projectObject);
  };

  /**
   * Check if user can manage project based on business unit permissions
   */
  const canManageProjectBasedOnBusinessUnit = (project) => {
    if (!project?.businessUnit?.id) return false;

    const businessUnitId = project.businessUnit.id;

    const projectObject = {
      businessUnitId: businessUnitId,
      __type: SUBJECTS.PROJECT
    };

    return ability.can(ACTIONS.MANAGE, projectObject);
  };

  /**
   * Check if user can manage project based on project-level permissions
   */
  const canManageProjectBasedOnProject = (project) => {
    if (!project?.id) return false;

    const projectObject = {
      projectId: project.id,
      __type: SUBJECTS.PROJECT
    };

    return ability.can(ACTIONS.UPDATE, projectObject);
  };

  /**
   * Check if user can manage special projects (Bench, Default Internal)
   */
  const canManageSpecialProjects = (project) => {
    if (!project || !project.businessUnit) return false;

    const businessUnitId = project.businessUnit.id;
    // Cluster the project's service unit belongs to. Lets a SpecialProject rule scope on
    // `clusterId` (e.g. `{ clusterId: { $in: clusterIds } }`) in addition to `businessUnitId`.
    const clusterId = project.businessUnit.cluster?.id ?? null;
    const isSpecialProject =
      project.projectPurpose?.code === PROJECT_PURPOSE_TYPES.BENCH
      || project.projectPurpose?.code === PROJECT_PURPOSE_TYPES.DEFAULT_INTERNAL;
    const noProjectManager = !project.manager;

    const specialProjectObject = {
      businessUnitId: businessUnitId,
      clusterId: clusterId,
      isSpecialProject: isSpecialProject,
      noProjectManager: noProjectManager,
      __type: SUBJECTS.SPECIAL_PROJECT
    };

    return ability.can(ACTIONS.UPDATE, specialProjectObject);
  };

  /**
   * Check if user can manage a project (combines all checks)
   */
  const canManageProject = (project) => {
    if (!project) return false;

    return canManageProjectBasedOnProject(project)
      || canManageProjectBasedOnBusinessUnit(project)
      || canManageProjectBasedOnCluster(project)
      || canManageAllProjects();
  };

  /**
   * Check if user can manage project code
   */
  const canManageProjectCode = (project) => {
    if (!project) return false;

    return ability.can(ACTIONS.UPDATE, SUBJECTS.PROJECT_CODE);
  };

  /**
   * Check if user can create project code
   */
  const canCreateProjectCode = () => {
    return ability.can(ACTIONS.CREATE, SUBJECTS.PROJECT_CODE);
  };

  /**
   * Check if user can delete project code
   */
  const canUpdateProjectCode = (project) => {
    if (!project) return false;

    return ability.can(ACTIONS.UPDATE, SUBJECTS.PROJECT_CODE);
  };

  /**
   * Check if user can manage project name
   */
  const canUpdateProjectName = (project) => {
    if (!project) return false;

    return ability.can(ACTIONS.UPDATE, SUBJECTS.PROJECT_NAME);
  };

  return {
    canManageProjectBasedOnCluster,
    canManageProjectBasedOnBusinessUnit,
    canManageProjectBasedOnProject,
    canManageSpecialProjects,
    canManageProject,
    canManageProjectCode,
    canCreateProjectCode,
    canUpdateProjectCode,
    canUpdateProjectName
  };
};
